import type { intelligenceRecords } from '@cwos/api-client';

/**
 * The sentences the record surfaces say, as PURE FUNCTIONS.
 *
 * They live here rather than inline in the screens for the reason
 * `insightsLogic.ts` and `forecastLogic.ts` give: a sentence that distinguishes
 * withheld from absent from zero is the thing most worth unit-testing, and a
 * sentence built inside JSX can only be tested by rendering something.
 *
 * Every function here returns `null` rather than an empty string when there is
 * nothing to say, so a caller renders nothing instead of an empty element.
 */

type OutcomeState = intelligenceRecords.OutcomeState;

/**
 * WITHHELD IS NOT ZERO, and this is where that is said out loud.
 *
 * `withheld` on both list routes counts records the caller may not read. An
 * operator who sees a short list must be able to tell "there is little advice"
 * from "there is advice you may not see"; silently filtering to nothing
 * conflates the two, which is this codebase's most common defect.
 */
export function withheldLine(withheld: number | undefined, noun: string): string | null {
  if (withheld === undefined || withheld === 0) return null;
  return withheld === 1
    ? `1 more ${noun} is withheld from you — it exists, and reading it needs permission you do not hold.`
    : `${withheld} more ${noun}s are withheld from you — they exist, and reading them needs permission you do not hold.`;
}

/**
 * A BOUNDED ANSWER THAT SAYS IT IS BOUNDED.
 *
 * Both list routes scan a window and report `scanned`. `windowExhausted` means
 * the window filled, so the answer is a page of a longer history rather than
 * the history.
 */
export function windowLine(scanned: number, windowExhausted: boolean): string | null {
  if (!windowExhausted) return null;
  return `Phibian looked at the ${scanned} most recent and stopped there. There may be older ones this answer does not cover.`;
}

/**
 * WHAT HAPPENED AFTER THE ADVICE — six states, six different sentences, and
 * none of them is "0".
 *
 * The distinction this function exists for is the four-state one:
 *
 *   not_yet_measured  ABSENT   — nothing was ever filed
 *   withheld          WITHHELD — it exists and you may not read it
 *   unreconciled      neither  — its reach was never computed, so NOBODY can
 *                                say who may read it. A grant does not fix this
 *                                and the sentence must not suggest asking for
 *                                one; provenance fixes it.
 *   unavailable       REFUSED  — the permission catalog could not be resolved,
 *                                so the store was not consulted at all
 *   not_applicable    the record declared no feasible action, so there is no
 *                                outcome to have
 *   measured          a real answer
 */
export function outcomeSentence(status: {
  state: OutcomeState;
  withheld: number;
  unreconciled: number;
  outcomes: readonly unknown[];
}): string {
  switch (status.state) {
    case 'measured':
      return status.outcomes.length === 1
        ? 'One outcome has been recorded against this recommendation.'
        : `${status.outcomes.length} outcomes have been recorded against this recommendation.`;
    case 'not_yet_measured':
      return 'No outcome has been recorded yet. That is not the same as the advice having failed — nobody has said what happened.';
    case 'not_applicable':
      return 'This record declared no feasible action, so there is no outcome for it to have.';
    case 'withheld':
      return `Something was recorded and you may not read it (${status.withheld} record${status.withheld === 1 ? '' : 's'}). This is not "no outcome".`;
    case 'unreconciled':
      return `${status.unreconciled} outcome record${status.unreconciled === 1 ? '' : 's'} cannot be shown because nobody has established who may read ${status.unreconciled === 1 ? 'it' : 'them'}. A permission would not resolve this.`;
    case 'unavailable':
      return 'The outcome store could not be consulted, so this says nothing about whether an outcome exists.';
  }
}

/**
 * Whether the confidence band was computed with the outcome store in hand.
 *
 * `consulted: false` means the band is a statement about the evidence alone. A
 * screen that showed the band without saying so would be reporting a
 * confidence whose basis it does not know.
 */
export function consultedLine(consulted: boolean): string {
  return consulted
    ? 'The outcome store was consulted when this band was computed.'
    : 'The outcome store was NOT consulted, so this band is about the evidence alone.';
}

/** The evidence band, in words rather than in the server's identifier. */
export function strengthLabel(strength: intelligenceRecords.EvidenceStrength): string {
  switch (strength) {
    case 'insufficient':
      return 'Insufficient evidence';
    case 'single_source':
      return 'One source';
    case 'corroborated':
      return 'Corroborated';
  }
}

/**
 * The band's tone, in the shared `Severity` vocabulary.
 *
 * `unsure` and not `urgent` for the weak bands: a thin evidence base is a
 * reason to read the caveat, not an alarm. The tone rule this product states
 * for Insights — "encourage investigation, not anxiety" — is the same rule
 * here, and a corroborated band is `plain` rather than `good` because a
 * well-evidenced recommendation is not thereby a correct one.
 */
export function strengthTone(strength: intelligenceRecords.EvidenceStrength): 'plain' | 'unsure' {
  return strength === 'corroborated' ? 'plain' : 'unsure';
}

/**
 * The manifest line for one retrieval.
 *
 * `parameterCount` is stated and never expanded: the recorded call parameters
 * do not cross the boundary, and a screen that omitted the count would turn a
 * deliberate withholding into a silence.
 */
export function evidenceLine(evidence: {
  toolName: string;
  scopePermission: string;
  rowCount: number;
  parameterCount: number;
}): string {
  const rows = evidence.rowCount === 1 ? 'returned 1 row' : `returned ${evidence.rowCount} rows`;
  const params =
    evidence.parameterCount === 1
      ? '1 parameter, not shown'
      : `${evidence.parameterCount} parameters, not shown`;
  return `${evidence.toolName} · read under ${evidence.scopePermission} · ${rows} · ${params}`;
}

/**
 * A learning signal's own audience.
 *
 * `permissionRequirements` is the union of the permissions of every outcome
 * record the signal cites, and a CHECK constraint refuses a signal that does
 * not carry them. Saying it on the screen is what makes "aggregation does not
 * widen an audience" a visible property rather than a claim in a migration.
 */
export function reachLine(permissionRequirements: readonly string[]): string {
  if (permissionRequirements.length === 0) {
    return 'This signal carries no permission requirements of its own.';
  }
  return `Readable by holders of ${permissionRequirements.join(' and ')} — inherited from the records it was derived from.`;
}

/** The period a signal speaks about, as a date range rather than two instants. */
export function periodLine(period: { start: string; end: string }): string {
  return `${period.start.slice(0, 10)} to ${period.end.slice(0, 10)}`;
}
